<template>
  <div
    ref="QRElementRef"
    spellcheck="false"
    class="group relative m-2 h-[37rem] w-[26rem] overflow-hidden border bg-white"
  >
    <div class="absolute top-0 h-1/2 w-full transform bg-[#1D71B8]" />

    <div class="relative mx-5 mt-8">
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyTitle')"
        class="mx-10 h-28 overflow-hidden break-words rounded border-2 border-transparent text-center text-neutral-100 focus:outline-none group-hover:border-dashed group-hover:border-neutral-40 group-hover:focus:border-primary-40"
        :class="[title && titleFontSize, title ? 'font-bold' : 'opacity-40']"
        @input="UpdateTitle"
      >
        {{ title }}
      </h1>
      <LoadingSpinner v-if="loading" class="mt-4 h-6 w-6 text-primary-40" aria-hidden="true" />

      <img v-else-if="qrDataImage" :src="qrDataImage" class="mx-auto mt-4 h-56 w-56" />
      <div v-else class="mx-auto mt-4 flex h-24 w-24 items-center justify-center rounded border-2 border-neutral-40">
        QR
      </div>
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyTag')"
        class="mx-6 h-7 overflow-hidden border-2 border-transparent text-center text-xs uppercase focus:outline-none group-hover:border-dashed group-hover:border-neutral-40 group-hover:focus:border-primary-40"
        @input="UpdateTag"
      >
        {{ tag }}
      </h1>
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyDescription')"
        class="mx-6 mt-2 h-14 break-words rounded border-2 border-transparent text-center focus:outline-none group-hover:border-dashed group-hover:border-neutral-40 group-hover:focus:border-primary-40"
        :class="[
          description && descriptionFontSize,
          description ? 'font-medium' : 'opacity-40',
          pdfMode ? 'overflow-visible pb-4' : 'overflow-hidden',
        ]"
        @input="UpdateDescription"
      >
        {{ description }}
      </h1>
      <div v-if="!windowConfig.whiteLabel" class="flex justify-center">
        <PerfitLogo class="h-16 w-16" color="#c5c5c5" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { watchEffect, ref, computed, inject } from "vue";
import QRCode from "qrcode";

//Components
import LoadingSpinner from "@atoms/LoadingSpinner.vue";

//Logo
import PerfitLogo from "../PerfitLogo.vue";

//Utils
import { useNotifications } from "@composables/notifications";
import { useI18n } from "vue-i18n";

const { notify } = useNotifications();
const { t } = useI18n();

//Props
const props = withDefaults(
  defineProps<{
    title?: string;
    description?: string;
    tag?: string;
    url: string;
    qrDataImage: string;
  }>(),
  {
    title: "",
    description: "",
    tag: "",
    url: "",
    qrDataImage: "",
  },
);

//Emits
const emit = defineEmits<{
  (e: "update:title", modelValue: string): void;
  (e: "update:description", modelValue: string): void;
  (e: "update:tag", modelValue: string): void;
  (e: "update:qrDataImage", modelValue: string): void;
}>();

const pdfMode = inject("pdfMode", false);
const windowConfig = computed(() => window.config);

const maxTitleLength = 240;
const UpdateTitle = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxTitleLength) (event.target as HTMLHeadElement).textContent = props.title;

  emit("update:title", (event.target as HTMLHeadElement)?.textContent || "");
};

const maxDescriptionLength = 120;
const UpdateDescription = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxDescriptionLength) (event.target as HTMLHeadElement).textContent = props.description;

  emit("update:description", (event.target as HTMLHeadElement)?.textContent || "");
};

const maxTagLength = 30;
const UpdateTag = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxTagLength) (event.target as HTMLHeadElement).textContent = props.tag;

  emit("update:tag", (event.target as HTMLHeadElement)?.textContent || "");
};

//Refs
const QRElementRef = ref();

//State
const loading = ref(false);

//Functions
const generateQR = async (text) => {
  try {
    loading.value = true;

    const QRImage = await QRCode.toDataURL(text, { errorCorrectionLevel: "H", scale: 12 });

    emit("update:qrDataImage", QRImage);

    loading.value = false;
  } catch (err) {
    loading.value = false;
    notify({
      title: t(`errors.server.title`),
      text: t(`errors.server.text`),
      theme: "error",
      timeout: 0,
    });
  }
};

const titleFontSize = computed(() => {
  if (props.title.length >= 180) {
    return "text-xs";
  }
  if (props.title.length >= 120) {
    return "text-sm";
  }
  if (props.title.length >= 100) {
    return "text-base";
  }
  if (props.title.length >= 80) {
    return "text-lg";
  }
  if (props.title.length >= 60) {
    return "text-xl";
  }
  if (props.title.length >= 40) {
    return "text-2xl";
  }
  return "text-3xl";
});

const descriptionFontSize = computed(() => {
  if (props.description.length >= 80) {
    return "text-xs";
  }
  if (props.description.length >= 60) {
    return "text-sm";
  }
  if (props.description.length >= 40) {
    return "text-base";
  }
  return "text-lg";
});

//Watchers
watchEffect(async () => {
  if (props.url) {
    await generateQR(props.url);
  } else {
    emit("update:qrDataImage", "");
  }
});
</script>

<style>
[contentEditable="true"]:empty:not(:focus):before {
  content: attr(data-placeholder);
}
</style>

<i18n lang="jsonc">
// TODO: i18n
{
  "es": {
    "emptyTitle": "Titulo",
    "emptyDescription": "Descripción",
    "emptyTag": "Etiqueta",
  },
  "pt": {
    "emptyTitle": "Título",
    "emptyDescription": "Descrição",
    "emptyTag": "Etiqueta",
  },
}
</i18n>
